import { getSupabaseClient } from "../../shared/api/supabase";
import { invokeV4, tableRows } from "../../shared/api/v4Remote";
import type { CreateDailyWorkInput, DailyWorkAssignment, DailyWorkEvidence, DailyWorkItem, DailyWorkState } from "./dailyWork.types";

type Row = Record<string, unknown>;
const text = (value: unknown) => String(value ?? "");
const allowedImages = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

export const remoteDailyWorkRepository = {
  async getState(): Promise<DailyWorkState> {
    const [items, assignments, evidence] = await Promise.all([
      tableRows<Row>("daily_work_items"),
      tableRows<Row>("daily_work_assignments"),
      tableRows<Row>("daily_work_evidence"),
    ]);
    const paths = evidence.map((row) => text(row.storage_path)).filter(Boolean);
    const signed = paths.length
      ? await getSupabaseClient().storage.from("daily-work-evidence").createSignedUrls(paths, 900)
      : { data: [], error: null };
    if (signed.error) throw new Error(signed.error.message);
    const urls = new Map((signed.data ?? []).map((entry, index) => [paths[index], entry.signedUrl]));
    return {
      items: items.map((row) => ({
        id: text(row.id), storeId: text(row.store_id), workDate: text(row.work_date),
        title: text(row.title), description: text(row.description), priority: row.priority,
        dueTime: row.due_time ? text(row.due_time).slice(0, 5) : null, status: row.status,
        createdBy: text(row.created_by), createdAt: text(row.created_at),
      }) as DailyWorkItem),
      assignments: assignments.map((row) => ({
        id: text(row.id), itemId: text(row.item_id), employeeId: text(row.employee_id),
        storeId: text(row.store_id), status: row.status, employeeNote: text(row.employee_note),
        submittedAt: row.submitted_at ? text(row.submitted_at) : null,
        reviewerId: row.reviewer_id ? text(row.reviewer_id) : null,
        reviewReason: text(row.review_reason), reviewedAt: row.reviewed_at ? text(row.reviewed_at) : null,
        updatedAt: text(row.updated_at),
      }) as DailyWorkAssignment),
      evidence: evidence.map((row) => ({
        id: text(row.id), assignmentId: text(row.assignment_id), employeeId: text(row.employee_id),
        storeId: text(row.store_id), storagePath: text(row.storage_path), fileName: text(row.file_name),
        mimeType: text(row.mime_type), fileSize: Number(row.file_size), previewUrl: urls.get(text(row.storage_path)) ?? "",
        uploadedAt: text(row.uploaded_at),
      }) as DailyWorkEvidence),
    };
  },
  createWork(_actor: unknown, input: CreateDailyWorkInput) {
    return invokeV4<{ id: string }>("v4-daily-work", "createWork", input);
  },
  markIncomplete(_actor: unknown, input: { assignmentId: string; note: string }) {
    return invokeV4<{ id: string }>("v4-daily-work", "markIncomplete", input);
  },
  async submitCompletion(_actor: unknown, input: { assignmentId: string; note: string; files: File[] }) {
    if (input.files.length > 6) throw new Error("每次最多上传6张图片。");
    for (const file of input.files) {
      if (!allowedImages.includes(file.type)) throw new Error("附件仅支持 JPG、PNG、WebP 或 HEIC 图片。");
      if (file.size < 1 || file.size > 10 * 1024 * 1024) throw new Error("单张图片不能超过10MB。");
      const prepared = await invokeV4<{ path: string; token: string }>("v4-daily-work", "createEvidenceUpload", {
        assignmentId: input.assignmentId, fileName: file.name, mimeType: file.type, fileSize: file.size,
      });
      const { error } = await getSupabaseClient().storage.from("daily-work-evidence")
        .uploadToSignedUrl(prepared.path, prepared.token, file, { contentType: file.type });
      if (error) throw new Error(error.message);
      await invokeV4<{ id: string }>("v4-daily-work", "finalizeEvidence", {
        assignmentId: input.assignmentId, path: prepared.path, fileName: file.name, mimeType: file.type, fileSize: file.size,
      });
    }
    return invokeV4<{ id: string }>("v4-daily-work", "submitCompletion", {
      assignmentId: input.assignmentId, note: input.note,
    });
  },
  reviewCompletion(_actor: unknown, input: { assignmentId: string; decision: "approve" | "reject"; reason: string }) {
    return invokeV4<{ id: string }>("v4-daily-work", "reviewCompletion", input);
  },
};
